import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { CreativeStatus } from 'components/common/types/Creative.types';
import { changeFilterValue, resetPageFilters } from '../store/pagesFilters/reducer';
import { Filter, PreFilterParseResult, UsePageFilters } from '../store/pagesFilters/types';
import { RootState } from '../store';

export const usePageFilters = <T>(
  pageName: string | null = null,
  parsePreFilters?: (params: URLSearchParams) => PreFilterParseResult<T> | null,
): UsePageFilters<T> => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const filterPageName = useMemo(
    () => (pageName || location.pathname || '').replace(/(^\/|\/$)/g, ''),
    [pageName, location.pathname],
  );

  const preFilter = useMemo(() => {
    if (parsePreFilters) {
      const result = parsePreFilters(searchParams);
      if (result) return result;
      return null;
    }

    if (searchParams.get('filter')) {
      return {
        filters: { status: CreativeStatus.PENDING } as unknown as Partial<Filter<T>>,
        consumedParams: ['filter'],
      };
    }

    return null;
  }, [searchParams, parsePreFilters]);

  const pageFilters = useSelector((state: RootState) => state.pagesFilters[filterPageName]);

  const changeFilters = (newFilters: Partial<Filter<T>>): void => {
    dispatch(
      changeFilterValue({
        [filterPageName]: newFilters,
      }),
    );
  };

  const resetFilters = (): void => {
    dispatch(resetPageFilters(filterPageName));
  };

  useEffect(() => {
    if (preFilter) {
      changeFilters(preFilter.filters as Partial<Filter<T>>);

      preFilter.consumedParams.forEach((param) => searchParams.delete(param));
      setSearchParams(searchParams);
    }
  }, [preFilter]);

  useEffect(() => {
    if (!pageFilters && !preFilter) resetFilters();
  }, [pageFilters]);

  return { filters: pageFilters || {}, changeFilters, resetFilters };
};
